import type { SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { aabb2ByCenterWidthHeight, aabb2Grow, aabb2AddPoint, aabb2Copy, aabb2Union, aabb2Move } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2AABB2, polyLine2Copy, polyLine2FromAABB2, polyLine2Move, polyLine2Rotate } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { DIM_LW } from "./constants";
import type { PlanCollider, CollisionType } from "./interiorLabelObstacle";
import {
  dimLabelFromGroup,
  dimLineFromGroup,
  transformRelaxedDimensionGroup,
  transformRelaxedSheetTextGeometry,
} from "./planDimensionGroupTools";
import type { CategorizedTextGeometry, GroupOfTaggedPlanGeometries } from "./planGeometrySourceTypes";
import { textSizeInView } from "./primitives/textPrimitive";

interface MakePlanColliderFromSheetGeometryProps {
  collisionType: CollisionType;
  labelBorder?: number;
  dimensionLineWeight?: number;
  baseLine?: PolyLine2;
  mass?: number;
  sheetTextGeomSource?: SheetTextGeometry & Required<Pick<SheetTextGeometry, "category">>;
  dimSheetGeomSource?: GroupOfTaggedPlanGeometries;
  moveOnDimLine?: boolean;
}

export function makePlanColliderFromSheetGeometry({
  collisionType,
  labelBorder = 0,
  dimensionLineWeight = DIM_LW,
  baseLine,
  mass,
  sheetTextGeomSource,
  dimSheetGeomSource,
  moveOnDimLine,
}: MakePlanColliderFromSheetGeometryProps): PlanCollider {
  let label: string | undefined;
  let fontSize: SheetInches | undefined;
  let rotationRads: number;
  let x: number | undefined;
  let y: number | undefined;
  let dimensionLine: PolyLine2 | undefined;
  let dimensionLineUnrelaxed: PolyLine2 | undefined;
  let rangeAABB: AABB2 | undefined;
  let normalX: number | undefined;
  let normalY: number | undefined;

  if (sheetTextGeomSource) {
    label = sheetTextGeomSource.text;
    fontSize = sheetTextGeomSource.fontSize;
    x = sheetTextGeomSource.x;
    y = sheetTextGeomSource.y;
    rotationRads = sheetTextGeomSource.rotation ?? 0;
    rangeAABB = sheetTextGeomSource.relaxationRangeAABB;
    normalX = sheetTextGeomSource.relaxationNormalX;
    normalY = sheetTextGeomSource.relaxationNormalY;
  } else {
    assertIsNotUndefined(dimSheetGeomSource);
    const dimLabel = dimLabelFromGroup(dimSheetGeomSource);
    assertIsNotUndefined(dimLabel);
    label = dimLabel.text;
    fontSize = dimLabel.fontSize;
    x = dimLabel.x;
    y = dimLabel.y;
    rotationRads = dimLabel.rotation ?? 0;
    const dimLine = dimLineFromGroup(dimSheetGeomSource);
    assertIsNotUndefined(dimLine);
    dimensionLine = polyLine2Copy(dimLine.paths[0]);
    dimensionLineUnrelaxed = polyLine2Copy(dimensionLine);
    rangeAABB = dimLabel.relaxationRangeAABB;
    normalX = dimLabel.relaxationNormalX;
    normalY = dimLabel.relaxationNormalY;
  }

  const [w, h] = textSizeInView(label, fontSize);
  const baseAABB = aabb2ByCenterWidthHeight(x, y, w, h);
  aabb2Grow(baseAABB, labelBorder, labelBorder);

  if (dimensionLine) {
    aabb2AddPoint(baseAABB, baseAABB.minX, baseAABB.minY - dimensionLineWeight);
  }

  const labelPerimeter = polyLine2Rotate(polyLine2FromAABB2(baseAABB), -rotationRads, x, y - dimensionLineWeight);
  const labelAABB = polyLine2AABB2(labelPerimeter);

  let fullAABB = aabb2Copy(labelAABB);
  if (dimensionLine) {
    fullAABB = aabb2Union(fullAABB, polyLine2AABB2(dimensionLine));
  }

  // if no rangeAABB is provided, prevent relaxation movement by
  // setting it to a minimum size
  rangeAABB ??= aabb2New(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  );

  rangeAABB = aabb2Union(rangeAABB, labelAABB);

  return {
    handle: sheetTextGeomSource?.handle,
    collisionType,
    rangeAABB,
    labelHeight: h,
    labelPerimeter,
    fontSize,
    labelAABB,
    fullAABB,
    label,
    baseLine,
    dimensionLine,
    dimensionLineUnrelaxed,
    labelRotation: rotationRads,
    mass,
    moveOnDimLine,
    normalX,
    normalY,
    planTextGeomSource: sheetTextGeomSource,
    dimPlanGeomSource: dimSheetGeomSource,
  };
}

// This function could be smarter and do constrained movements based on which collider props are available
export function movePlanCollider(collider: PlanCollider, dx: number, dy: number) {
  polyLine2Move(collider.labelPerimeter, dx, dy);
  aabb2Move(collider.labelAABB, dx, dy);
}

export function extractRelaxedSheetGeometryFromColliders(colliderGroups: PlanCollider[][]): {
  labels: CategorizedTextGeometry[];
  groups: GroupOfTaggedPlanGeometries[];
} {
  const groups: GroupOfTaggedPlanGeometries[] = [];
  const labels: CategorizedTextGeometry[] = [];

  for (const group of colliderGroups) {
    for (const collider of group) {
      if (collider.planTextGeomSource) {
        const relaxedGeom = transformRelaxedSheetTextGeometry(collider);
        if (relaxedGeom) {
          labels.push(relaxedGeom);
        }
      }

      if (collider.dimPlanGeomSource) {
        groups.push({ geometries: transformRelaxedDimensionGroup(collider) });
      }
    }
  }

  return { labels, groups };
}

export function planColliderGroupHandler(
  colliders: (PlanCollider | undefined)[],
  groups: PlanCollider[][] = [],
  appendToPrevGroup: boolean = false,
) {
  if (!appendToPrevGroup || groups.length === 0) {
    colliders.forEach((toAdd) => {
      if (toAdd) {
        groups.push([toAdd]);
      }
    });
  } else {
    colliders.forEach((toAdd) => {
      if (toAdd) {
        groups[groups.length - 1].push(toAdd);
      }
    });
  }
}
